import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  TextField,
} from "@aws-amplify/ui-react";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { LayoutContext } from "layout/context/LayoutContext";
import { API, graphqlOperation } from "aws-amplify";
import { listCompanies } from "graphql/queries";
import toast from "react-hot-toast";
import { globalStrings } from "config/translations/es";

const CostCenterForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [costCenterForm, setCostCenterForm] = useState(initialValues);
  const [errors, setErrors] = useState({
    code: "",
    name: "",
    companyID: "",
  });
  const validateErrors = useErrors({
    code: "isRequired",
    name: "isRequired",
    companyID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(costCenterForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (
      !companies.some(
        (companyRec) => companyRec.id === costCenterForm.companyID
      )
    ) {
      setErrors((prevSt) => ({
        ...prevSt,
        companyID: strings.validations.incorrectValue,
      }));
      return;
    }

    if (!hasErrors)
      onSubmit({
        ...costCenterForm,
        companyID: costCenterForm.companyID || null,
      });
  };

  const onClearSelect = () => {
    const value = "";
    fetchCompanies({ name: value });
    setCostCenterForm((prevSt) => ({
      ...prevSt,
      companyID: value,
    }));
  };

  const onSelect = (name, value) => {
    setCostCenterForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    fetchCompanies({ name: value });
    setCostCenterForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setCostCenterForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      code: "",
      companyID: "",
    });
    setCostCenterForm(initialValues);
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompaniesLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies(companiesItems);
      setCompaniesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    }
  }

  useEffect(() => {
    setCostCenterForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid
          columnGap="15px"
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
        >
          <TextField
            name="code"
            label={<LabelRequired text={strings.fields.code.label} />}
            value={costCenterForm.code}
            onChange={onChange}
            errorMessage={errors.code}
            hasError={!!errors.code}
          ></TextField>
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={costCenterForm.name}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          ></TextField>
        </Grid>
        {type === "create" && (
          <>
            <Label htmlFor="companyID">
              {<LabelRequired text={strings.fields.company.label} />}
            </Label>
            <div style={{ marginTop: "-5px" }}>
              <Autocomplete
                id="companyID"
                name="companyID"
                label={<LabelRequired text={strings.fields.company.label} />}
                isRequired
                placeholder={strings.fields.company.placeholder}
                value={
                  companies.find(
                    (companyRec) => companyRec.id === costCenterForm.companyID
                  )?.name ??
                  costCenterForm.companyID ??
                  ""
                }
                options={companies.map((companyRec) => ({
                  id: companyRec.id,
                  label: companyRec.name,
                }))}
                isLoading={companiesLoading}
                onClear={() => onClearSelect("companyID")}
                onChange={onChangeSelect}
                onSelect={({ id }) => onSelect("companyID", id)}
                errorMessage={errors?.companyID}
                hasError={!!errors?.companyID}
                menuSlots={{ Empty: globalStrings.noResults }}
              ></Autocomplete>
            </div>
          </>
        )}
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
    },
    fields: {
      name: {
        label: "Nombre",
      },
      code: {
        label: "Código",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
  },
});

export default CostCenterForm;
